import type { ApiErrorBody } from '@hvac/contracts';
import { ERROR_CODES, type ErrorCode } from '@hvac/core';

/**
 * The client-side representation of a failed request.
 *
 * Carries the machine code and the request id so a component can branch on the
 * former and display the latter. Never construct one from a message string
 * alone, the code is what UI logic must depend on.
 */
export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly httpStatus: number;
  readonly requestId: string;
  readonly details: Readonly<Record<string, unknown>> | undefined;

  constructor(params: { body: ApiErrorBody; httpStatus: number; requestId: string }) {
    super(params.body.message);
    this.name = 'ApiError';
    this.code = params.body.code;
    this.httpStatus = params.httpStatus;
    this.requestId = params.requestId;
    this.details = params.body.details;
  }
}

/** Raised when the network is unreachable or the response was not parseable. */
export class NetworkError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = 'NetworkError';
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

/** True when the failure means "sign in again", so the UI can redirect. */
export function isAuthenticationFailure(value: unknown): boolean {
  return isApiError(value) && value.code === ERROR_CODES.UNAUTHENTICATED;
}

/**
 * A message safe and useful to put in front of a user.
 *
 * Server messages for exposed errors are already written for humans. Anything
 * else gets a generic sentence, because the raw text of an unexpected failure
 * helps nobody and often alarms.
 */
export function toDisplayMessage(error: unknown): string {
  if (error instanceof NetworkError) {
    return 'Could not reach the server. Check your connection and try again.';
  }
  if (isApiError(error)) {
    switch (error.code) {
      case ERROR_CODES.UNAUTHENTICATED:
        return 'Your session has expired. Please sign in again.';
      case ERROR_CODES.FORBIDDEN:
        return error.message;
      case ERROR_CODES.RATE_LIMITED:
        return 'Too many requests. Please wait a moment and try again.';
      case ERROR_CODES.VALIDATION_FAILED:
      case ERROR_CODES.NOT_FOUND:
      case ERROR_CODES.CONFLICT:
        return error.message;
      case ERROR_CODES.DEPENDENCY_FAILURE:
      case ERROR_CODES.INTERNAL:
        return 'Something went wrong on our side. The reference below will help support.';
      default:
        return error.message;
    }
  }
  return 'An unexpected error occurred.';
}
